export type ReceiptListItem = {
  id: string;
  ts: string;
  siteId: string;
  subjectKey: string;
  hash: string;
  prevHash: string | null;
  keyId: string;
  payload?: Record<string, unknown>;
  tcfString?: string | null;
  gppString?: string | null;
  gvlVersion?: number | null;
};

export async function fetchReceipts(params: {
  baseUrl: string;
  siteId?: string;
  from?: string;
  to?: string;
  cursor?: string | null;
  limit?: number;
  bearer?: string;
}): Promise<{ items: ReceiptListItem[]; nextCursor: string | null }> {
  const u = new URL('/api/admin/receipts', params.baseUrl);
  if (params.siteId) u.searchParams.set('siteId', params.siteId);
  if (params.from) u.searchParams.set('from', params.from);
  if (params.to) u.searchParams.set('to', params.to);
  if (params.cursor) u.searchParams.set('cursor', params.cursor);
  if (params.limit) u.searchParams.set('limit', String(params.limit));
  const res = await fetch(u.toString(), {
    headers: params.bearer ? { Authorization: `Bearer ${params.bearer}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = (await res.json()) as {
    items?: Array<Record<string, unknown>>;
    nextCursor?: string | null;
  };
  // normalize ts to ISO string, guard unknown shapes
  const items = (j.items || []).map((r) => {
    const obj = r as Record<string, unknown>;
    const tsRaw = obj['ts'];
    const ts = typeof tsRaw === 'string' ? tsRaw : new Date(String(tsRaw || '')).toISOString();
    return {
      id: String(obj['id'] ?? ''),
      ts,
      siteId: String(obj['siteId'] ?? ''),
      subjectKey: String(obj['subjectKey'] ?? ''),
      hash: String(obj['hash'] ?? ''),
      prevHash: (obj['prevHash'] as string | null) ?? null,
      keyId: String(obj['keyId'] ?? ''),
      payload: (obj['payload'] as Record<string, unknown> | undefined) ?? undefined,
      tcfString: (obj['tcfString'] as string | null | undefined) ?? null,
      gppString: (obj['gppString'] as string | null | undefined) ?? null,
      gvlVersion: (obj['gvlVersion'] as number | null | undefined) ?? null,
    } as ReceiptListItem;
  });
  return { items, nextCursor: j.nextCursor ?? null };
}

export async function verifyReceiptsRange(params: {
  baseUrl: string;
  siteId: string;
  subjectKey: string;
  from?: string;
  to?: string;
  bearer?: string;
}) {
  const res = await fetch(`${params.baseUrl}/api/admin/receipts/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(params.bearer ? { Authorization: `Bearer ${params.bearer}` } : {}),
    },
    body: JSON.stringify({
      siteId: params.siteId,
      subjectKey: params.subjectKey,
      from: params.from,
      to: params.to,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as { ok: boolean; errors?: string[] };
}
